'use client'

import { useState, useCallback, useRef } from 'react'
import { useSession } from 'next-auth/react'
import Button from '@/components/ui/Button'
import Container from '@/components/ui/Container'

interface PhotoUploaderProps {
  onUploadComplete?: (photo: any) => void
  onUploadError?: (error: string) => void
  clubId?: string
  eventId?: string
  catchId?: string
  className?: string
  maxFiles?: number
  showMetadata?: boolean
}

interface UploadProgress {
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error'
  error?: string
  photoId?: string
}

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10MB
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']

export default function PhotoUploader({
  onUploadComplete,
  onUploadError,
  clubId,
  eventId,
  catchId,
  className = '',
  maxFiles = 5,
  showMetadata = true
}: PhotoUploaderProps) {
  const { data: session } = useSession()
  const [files, setFiles] = useState<File[]>([])
  const [uploads, setUploads] = useState<UploadProgress[]>([])
  const [isDragOver, setIsDragOver] = useState(false)
  const [metadata, setMetadata] = useState({
    caption: '',
    tags: [] as string[],
    isPublic: false
  })
  const [tagInput, setTagInput] = useState('')
  const fileInputRef = useRef<HTMLInputElement>(null)

  const validateFile = (file: File): string | null => {
    if (!ALLOWED_TYPES.includes(file.type)) {
      return `Invalid file type. Allowed: ${ALLOWED_TYPES.join(', ')}`
    }
    if (file.size > MAX_FILE_SIZE) {
      return `File too large. Maximum size: ${Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB`
    }
    return null
  }

  const handleFiles = useCallback((newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles)
    const validFiles: File[] = []
    const errors: string[] = []

    fileArray.forEach(file => {
      const error = validateFile(file)
      if (error) {
        errors.push(`${file.name}: ${error}`)
      } else {
        validFiles.push(file)
      }
    })

    if (errors.length > 0) {
      onUploadError?.(errors.join('\n'))
    }

    if (validFiles.length > 0) {
      const totalFiles = files.length + validFiles.length
      if (totalFiles > maxFiles) {
        onUploadError?.(`Maximum ${maxFiles} files allowed`)
        return
      }

      setFiles(prev => [...prev, ...validFiles])
      setUploads(prev => [
        ...prev,
        ...validFiles.map(file => ({
          file,
          progress: 0,
          status: 'pending' as const
        }))
      ])
    }
  }, [files.length, maxFiles, onUploadError])

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(true)
  }, [])

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
  }, [])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setIsDragOver(false)
    handleFiles(e.dataTransfer.files)
  }, [handleFiles])

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      handleFiles(e.target.files)
    }
  }, [handleFiles])

  const removeFile = (index: number) => {
    setFiles(prev => prev.filter((_, i) => i !== index))
    setUploads(prev => prev.filter((_, i) => i !== index))
  }

  const addTag = () => {
    const tag = tagInput.trim()
    if (tag && !metadata.tags.includes(tag) && metadata.tags.length < 10) {
      setMetadata(prev => ({
        ...prev,
        tags: [...prev.tags, tag]
      }))
      setTagInput('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setMetadata(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const uploadFile = async (file: File, index: number) => {
    try {
      // Update status to uploading
      setUploads(prev => prev.map((upload, i) => 
        i === index ? { ...upload, status: 'uploading' } : upload
      ))

      // Get presigned URL
      const response = await fetch('/api/photos/upload', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          fileName: file.name,
          fileSize: file.size,
          mimeType: file.type,
          clubId,
          eventId,
          catchId,
          caption: metadata.caption,
          tags: metadata.tags,
          isPublic: metadata.isPublic
        })
      })

      if (!response.ok) {
        const error = await response.json()
        throw new Error(error.error || 'Failed to get upload URL')
      }

      const { uploadUrl, photoId } = await response.json()

      // Update status to processing
      setUploads(prev => prev.map((upload, i) => 
        i === index ? { ...upload, status: 'processing', photoId } : upload
      ))

      // Upload to S3
      const uploadResponse = await fetch(uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type
        }
      })

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload to S3')
      }

      // Update status to completed
      setUploads(prev => prev.map((upload, i) => 
        i === index ? { ...upload, status: 'completed', progress: 100 } : upload
      ))

      onUploadComplete?.({
        id: photoId,
        fileName: file.name,
        fileSize: file.size,
        mimeType: file.type
      })

    } catch (error) {
      console.error('Upload error:', error)
      setUploads(prev => prev.map((upload, i) => 
        i === index ? { 
          ...upload, 
          status: 'error', 
          error: error instanceof Error ? error.message : 'Upload failed'
        } : upload
      ))
      onUploadError?.(error instanceof Error ? error.message : 'Upload failed')
    }
  }

  const startUploads = async () => {
    if (!session) {
      onUploadError?.('You must be logged in to upload photos')
      return
    }

    for (let i = 0; i < files.length; i++) {
      await uploadFile(files[i], i)
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  if (!session) {
    return (
      <div className={`p-6 border-2 border-dashed border-gray-300 rounded-lg text-center ${className}`}>
        <p className="text-gray-500">Please log in to upload photos</p>
      </div>
    )
  }

  return (
    <Container className={className}>
      <div className="space-y-6">
        {/* Drag and Drop Area */}
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
            isDragOver
              ? 'border-ocean-500 bg-ocean-50'
              : 'border-gray-300 hover:border-gray-400'
          }`}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          <div className="space-y-4">
            <div className="text-4xl">📸</div>
            <div>
              <p className="text-lg font-medium text-gray-900">
                Drop photos here or click to browse
              </p>
              <p className="text-sm text-gray-500 mt-1">
                Supports JPEG, PNG, WebP up to {Math.round(MAX_FILE_SIZE / 1024 / 1024)}MB
              </p>
            </div>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="secondary"
              size="sm"
            >
              Choose Files
            </Button>
          </div>
        </div>

        <input
          ref={fileInputRef}
          type="file"
          multiple
          accept={ALLOWED_TYPES.join(',')}
          onChange={handleFileSelect}
          className="hidden"
        />

        {/* Metadata Form */}
        {showMetadata && (
          <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
            <h3 className="font-medium text-gray-900">Photo Details</h3>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Caption
              </label>
              <textarea
                value={metadata.caption}
                onChange={(e) => setMetadata(prev => ({ ...prev, caption: e.target.value }))}
                placeholder="Add a caption for your photos..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-500"
                rows={3}
                maxLength={500}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tags
              </label>
              <div className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())}
                  placeholder="Add a tag..."
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-ocean-500"
                />
                <Button
                  onClick={addTag}
                  variant="secondary"
                  size="sm"
                  disabled={!tagInput.trim() || metadata.tags.length >= 10}
                >
                  Add
                </Button>
              </div>
              {metadata.tags.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {metadata.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center gap-1 px-2 py-1 bg-ocean-100 text-ocean-800 text-sm rounded-full"
                    >
                      {tag}
                      <button
                        onClick={() => removeTag(tag)}
                        className="text-ocean-600 hover:text-ocean-800"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}
            </div>

            <div className="flex items-center">
              <input
                type="checkbox"
                id="isPublic"
                checked={metadata.isPublic}
                onChange={(e) => setMetadata(prev => ({ ...prev, isPublic: e.target.checked }))}
                className="h-4 w-4 text-ocean-600 focus:ring-ocean-500 border-gray-300 rounded"
              />
              <label htmlFor="isPublic" className="ml-2 block text-sm text-gray-700">
                Make photos public (visible to everyone)
              </label>
            </div>
          </div>
        )}

        {/* File List */}
        {files.length > 0 && (
          <div className="space-y-3">
            <h3 className="font-medium text-gray-900">
              Selected Files ({files.length}/{maxFiles})
            </h3>
            {files.map((file, index) => {
              const upload = uploads[index]
              return (
                <div key={index} className="flex items-center justify-between p-3 bg-white border rounded-lg">
                  <div className="flex items-center space-x-3">
                    <div className="text-2xl">🖼️</div>
                    <div>
                      <p className="font-medium text-gray-900">{file.name}</p>
                      <p className="text-sm text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-3">
                    {upload && (
                      <div className="flex items-center space-x-2">
                        {upload.status === 'uploading' && (
                          <div className="w-4 h-4 border-2 border-ocean-600 border-t-transparent rounded-full animate-spin"></div>
                        )}
                        {upload.status === 'processing' && (
                          <div className="w-4 h-4 border-2 border-yellow-600 border-t-transparent rounded-full animate-spin"></div>
                        )}
                        {upload.status === 'completed' && (
                          <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">✓</span>
                          </div>
                        )}
                        {upload.status === 'error' && (
                          <div className="w-4 h-4 bg-red-500 rounded-full flex items-center justify-center">
                            <span className="text-white text-xs">!</span>
                          </div>
                        )}
                        <span className="text-sm text-gray-500 capitalize">
                          {upload.status}
                        </span>
                      </div>
                    )}
                    <Button
                      onClick={() => removeFile(index)}
                      variant="ghost"
                      size="sm"
                      disabled={upload?.status === 'uploading' || upload?.status === 'processing'}
                    >
                      Remove
                    </Button>
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* Upload Button */}
        {files.length > 0 && (
          <div className="flex justify-end">
            <Button
              onClick={startUploads}
              disabled={uploads.some(upload => upload.status === 'uploading' || upload.status === 'processing')}
              className="px-6"
            >
              {uploads.some(upload => upload.status === 'uploading' || upload.status === 'processing')
                ? 'Uploading...'
                : `Upload ${files.length} Photo${files.length > 1 ? 's' : ''}`
              }
            </Button>
          </div>
        )}
      </div>
    </Container>
  )
}


